import { Copy, ExternalLink } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { type TransactionDetailsProps } from '../../types/transactions'
import { cn } from '../../lib/utils'
import { formatDate } from '../../lib/transaction-utils'
import { formatDisplayBps } from '../../utils/numbers'

// Helper function to determine chain from currency/symbol
// TODO: remove once we receive the chain
function getChainFromCurrency(
  currency: string,
): 'BTC' | 'ETH' | 'TRON' | 'BSC' | 'POLYGON' | 'SOLANA' {
  switch (currency?.toUpperCase()) {
    case 'BTC':
      return 'BTC'
    case 'USDC':
    case 'USDT':
    case 'ETH':
      return 'ETH'
    case 'BSC':
    case 'BNB':
      return 'BSC'
    case 'POL':
      return 'POLYGON'
    case 'SOL':
      return 'SOLANA'
    case 'TRON':
      return 'TRON'
    default:
      return currency as 'BTC' | 'ETH' | 'TRON' | 'BSC' | 'POLYGON' | 'SOLANA'
  }
}

// Helper function to generate explorer URLs
function getExplorerUrl(
  value: string,
  chain: 'BTC' | 'ETH' | 'TRON' | 'BSC' | 'POLYGON' | 'SOLANA' | null,
  fieldKey: string,
): string | null {
  if (!value || value === '-' || !chain) return null

  // Determine if it's a transaction hash or address based on field key and value format
  const isAddress =
    fieldKey === 'address' ||
    (value.startsWith('0x') && value.length === 42) ||
    (value.startsWith('1') && value.length >= 26 && value.length <= 35) ||
    (value.startsWith('T') && value.length === 34)

  const isTransactionHash =
    (fieldKey === 'hash' || (value.length >= 32 && !value.startsWith('0x'))) && !isAddress

  switch (chain) {
    case 'BTC':
      if (isTransactionHash) {
        return `https://mempool.space/tx/${value}`
      } else if (isAddress) {
        return `https://mempool.space/address/${value}`
      }
      break
    case 'ETH':
      if (isTransactionHash) {
        return `https://etherscan.io/tx/${value}`
      } else if (isAddress) {
        return `https://etherscan.io/address/${value}`
      }
      break
    case 'BSC':
      if (isTransactionHash) {
        return `https://bscscan.com/tx/${value}`
      } else if (isAddress) {
        return `https://bscscan.com/address/${value}`
      }
      break
    case 'TRON':
      if (isTransactionHash) {
        return `https://tronscan.org/#/transaction/${value.replace(/^0x/i, '')}`
      } else if (isAddress) {
        return `https://tronscan.org/#/address/${value}`
      }
      break
    case 'POLYGON':
      // ponytail: Amoy explorer when UI gains a testnet flag (ETH/BSC explorers are mainnet-only here too)
      if (isTransactionHash) {
        return `https://polygonscan.com/tx/${value}`
      } else if (isAddress) {
        return `https://polygonscan.com/address/${value}`
      }
      break
    case 'SOLANA':
      // ponytail: mainnet solscan only until UI gains a testnet/cluster flag (append ?cluster=devnet then)
      if (isTransactionHash) {
        return `https://solscan.io/tx/${value}`
      } else if (isAddress) {
        return `https://solscan.io/account/${value}`
      }
      break
  }

  return null
}

export function TransactionDetailsCard({
  title = 'Details',
  fields,
  onCopy,
  chain,
}: TransactionDetailsProps & { title?: string; chain?: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {fields.map((field) => {
          const detectedChain = getChainFromCurrency(chain || '')
          const explorerUrl = getExplorerUrl(field.value, detectedChain, field.fieldKey)

          return (
            <div key={field.fieldKey}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-sm text-muted-foreground">{field.label}</span>

                <div className="flex items-center gap-1">
                  {explorerUrl && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn(
                        'text-xs text-card-text border border-card-border rounded-md px-2 py-1 w-fit cursor-pointer',
                        'h-6 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
                      )}
                      onClick={() => window.open(explorerUrl, '_blank', 'noopener,noreferrer')}
                    >
                      <ExternalLink className="h-3 w-3" />
                      <span className="text-xs">View</span>
                    </Button>
                  )}

                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                      'text-xs text-card-text border border-card-border rounded-md px-2 py-1 w-fit cursor-pointer',
                      'h-6 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
                    )}
                    onClick={() => onCopy(field.value, field.fieldKey)}
                  >
                    <Copy className="h-3 w-3" />
                    <span className="text-xs">Copy</span>
                  </Button>
                </div>
              </div>
              <div className="text-sm break-all">{field.value}</div>
            </div>
          )
        })}
      </CardContent>
    </Card>
  )
}

interface SwapSummaryProps {
  fee: number
  amountTraded: string
  amountTradedSymbol: string
  price: string
  priceSymbol: string
}

interface TransactionInfoCardProps {
  user?: string
  company: string
  group?: string
  decisionBy?: string
  transactionId: string
  creationDate: string
  decisionDate?: string
  finalizedDate?: string
  onCopy: (text: string, field: string) => void
  swapSummary?: SwapSummaryProps
}

export function TransactionInfoCard({
  user,
  company,
  group,
  decisionBy,
  transactionId,
  creationDate,
  decisionDate,
  finalizedDate,
  onCopy,
  swapSummary,
}: TransactionInfoCardProps) {
  const isValidDate = (date: string) => {
    return date && date !== 'N/A' && date !== '-'
  }

  return (
    <Card>
      <CardContent>
        <CardTitle className="text-xl font-semibold mb-4">Info</CardTitle>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3">
            {user && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-card-text">User</span>
                <div className="font-medium">{user}</div>
              </div>
            )}
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm text-card-text">Company</span>
              <div className="font-medium">{company}</div>
            </div>
            {group && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-card-text">Group</span>
                <div className="font-medium">{group}</div>
              </div>
            )}
            {decisionBy && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-card-text">Decision by</span>
                <div className="font-medium">{decisionBy}</div>
              </div>
            )}
          </div>

          <div className="f">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm text-card-text">Transaction ID</span>
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'text-xs text-card-text border border-card-border rounded-md px-2 py-1 w-fit cursor-pointer',
                  'h-6 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
                )}
                onClick={() => onCopy(transactionId, 'transactionId')}
              >
                <Copy className="h-3 w-3" />
                <span className="text-xs">Copy</span>
              </Button>
            </div>
            <div className="text-sm break-all">{transactionId}</div>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm text-card-text">Creation Date</span>
              <div className="font-medium">
                {isValidDate(creationDate) ? formatDate(creationDate) : '-'}
              </div>
            </div>
            {decisionDate && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-card-text">Decision Date</span>
                <div className="font-medium">
                  {isValidDate(decisionDate) ? formatDate(decisionDate) : '-'}
                </div>
              </div>
            )}
            {finalizedDate && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-card-text">Finalized Date</span>
                <div className="font-medium">
                  {isValidDate(finalizedDate) ? formatDate(finalizedDate) : '-'}
                </div>
              </div>
            )}
          </div>

          {swapSummary && (
            <div className="grid grid-cols-1 gap-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-card-text">Fee</span>
                <div className="font-medium">{formatDisplayBps(swapSummary.fee)} BPS</div>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-card-text">Amount Traded</span>
                <div className="font-medium">
                  {swapSummary.amountTraded} {swapSummary.amountTradedSymbol}
                </div>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-card-text">Price</span>
                <div className="font-medium">
                  {swapSummary.price} {swapSummary.priceSymbol}
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
